import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Archive, Pencil, Plus } from 'lucide-react';
import { adminQueries, getErrorMessage, keys } from '@blimo/api-client';
import { useApi, useMe } from '@blimo/api-client/react';
import { formatDate, formatNumber, hasPermission, labelOf, METER_SOURCE_LABELS, nameSchema } from '@blimo/shared';
import {
  applyProblemToForm,
  Badge,
  Button,
  Checkbox,
  ConfirmDialog,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErrorState,
  Form,
  FormRootError,
  InputField,
  Label,
  PageHeader,
  SearchInput,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
} from '@blimo/ui';
import { FilterBar, FilterSelect } from '../../components/filter-bar.jsx';
import { PropertyLink } from '../../components/data.jsx';
import { PropertyFilter } from '../finance/panels.jsx';
import { useListState } from '../../lib/list-search.js';
import { MeterSheet } from './meter-sheet.jsx';
import { meterLabel, useSupplierOptions } from './utils.js';

/** Zähler › Zähler & Versorger (F-901, F-902). */
export function MetersListPage() {
  const search = useSearch({ strict: false });
  const navigate = useNavigate();
  const tab = search.tab === 'versorger' ? 'versorger' : 'zaehler';
  const setTab = (next) => navigate({ to: '/zaehler', search: { tab: next === 'versorger' ? 'versorger' : undefined }, replace: true });

  return (
    <>
      <PageHeader title="Zähler & Versorger" description="Zähler je Liegenschaft mit Versorger und Ableser; Versorger-Stammdaten." />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList aria-label="Zähler oder Versorger">
          <TabsTrigger value="zaehler">Zähler</TabsTrigger>
          <TabsTrigger value="versorger">Versorger</TabsTrigger>
        </TabsList>
        <TabsContent value="zaehler">
          <MetersTab search={search} />
        </TabsContent>
        <TabsContent value="versorger">
          <SuppliersTab />
        </TabsContent>
      </Tabs>
    </>
  );
}

function MetersTab({ search }) {
  const api = useApi();
  const { data: me } = useMe();
  const navigate = useNavigate();
  const list = useListState(search);
  const supplierOptions = useSupplierOptions();
  const canManage = hasPermission(me, 'meters.manage');
  const [editing, setEditing] = useState(/** @type {any} */ (undefined));
  const [archiving, setArchiving] = useState(/** @type {any} */ (null));
  const queryClient = useQueryClient();
  const params = {
    page: list.apiParams.page,
    per_page: list.apiParams.per_page,
    filter: { search: search.q, property_id: search.property, supplier_id: search.supplier, archived: search.archived === '1' ? true : undefined },
  };
  const { data, error, isPending, isFetching, refetch } = useQuery(adminQueries.meters(api, params));
  const columns = useMemo(() => meterColumns({ canManage, onEdit: setEditing, onArchive: setArchiving }), [canManage]);

  async function archive() {
    try {
      await api.admin.meters.archive(archiving.id);
      await queryClient.invalidateQueries({ queryKey: keys.admin.resource('meters') });
      queryClient.invalidateQueries({ queryKey: keys.admin.resource('meter-readings') });
      toast.success(`Zähler ${archiving.number} archiviert`);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Der Zähler konnte nicht archiviert werden.'));
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <FilterBar>
        <SearchInput label="Zähler suchen" placeholder="Zähler- oder Vertragsnummer …" value={search.q ?? ''} onValueChange={(v) => list.setFilter('q', v)} />
        <PropertyFilter value={search.property} onChange={(v) => list.setFilter('property', v)} />
        <FilterSelect label="Versorger" value={search.supplier} onChange={(v) => list.setFilter('supplier', v)} options={supplierOptions} />
        <div className="flex h-(--control-height-sm) items-center gap-2">
          <Checkbox id="meters-archived" checked={search.archived === '1'} onChange={(e) => list.setFilter('archived', e.target.checked ? '1' : undefined)} />
          <Label htmlFor="meters-archived" className="font-normal">
            Archivierte anzeigen
          </Label>
        </div>
        {canManage && (
          <Button size="sm" className="sm:ml-auto" onClick={() => setEditing(null)}>
            <Plus aria-hidden="true" /> Zähler anlegen
          </Button>
        )}
      </FilterBar>
      {error ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : (
        <DataTable
          label="Zähler"
          columns={columns}
          data={data?.data}
          rowCount={data?.meta?.total}
          isLoading={isPending}
          isFetching={isFetching}
          getRowId={(row) => String(row.id)}
          pagination={list.pagination}
          onPaginationChange={list.onPaginationChange}
          emptyTitle="Keine Zähler"
          emptyDescription="Für die gewählten Filter gibt es keine Zähler."
        />
      )}
      <MeterSheet
        open={editing !== undefined}
        onOpenChange={(open) => !open && setEditing(undefined)}
        meter={editing ?? undefined}
        onSaved={(meter) => !editing && meter?.id && navigate({ to: '/zaehler/$meterId', params: { meterId: String(meter.id) } })}
      />
      <ConfirmDialog
        open={Boolean(archiving)}
        onOpenChange={(open) => !open && setArchiving(null)}
        title="Zähler archivieren?"
        description={
          archiving ? `Zähler ${archiving.number} wird archiviert. Offene Ablesungen werden entfernt, die Ablese-Historie bleibt erhalten.` : undefined
        }
        confirmLabel="Archivieren"
        destructive
        onConfirm={archive}
      />
    </div>
  );
}

function meterColumns({ canManage, onEdit, onArchive }) {
  return [
    {
      id: 'number',
      header: 'Zähler',
      enableSorting: false,
      cell: ({ row }) => (
        <span className="flex flex-wrap items-center gap-1.5">
          <Link to="/zaehler/$meterId" params={{ meterId: String(row.original.id) }} className="font-medium text-primary hover:underline">
            {meterLabel(row.original)}
          </Link>
          {row.original.archived_at && <Badge variant="neutral">Archiviert</Badge>}
        </span>
      ),
    },
    { id: 'property', header: 'Liegenschaft', enableSorting: false, cell: ({ row }) => <PropertyLink property={row.original.property} /> },
    { id: 'contract_number', header: 'Vertrag', enableSorting: false, cell: ({ row }) => row.original.contract_number ?? '–' },
    { id: 'supplier', header: 'Versorger', enableSorting: false, cell: ({ row }) => row.original.supplier?.name ?? '–' },
    { id: 'reader', header: 'Ableser', enableSorting: false, cell: ({ row }) => row.original.reader?.name ?? '–' },
    { id: 'source', header: 'Quelle', enableSorting: false, cell: ({ row }) => labelOf(METER_SOURCE_LABELS, row.original.source) },
    {
      id: 'actions',
      header: <span className="sr-only">Aktionen</span>,
      enableSorting: false,
      cell: ({ row }) =>
        canManage && !row.original.archived_at ? (
          <span className="flex justify-end gap-1">
            <Button size="sm" variant="ghost" aria-label={`Zähler ${row.original.number} bearbeiten`} onClick={() => onEdit(row.original)}>
              <Pencil aria-hidden="true" />
            </Button>
            <Button size="sm" variant="ghost" aria-label={`Zähler ${row.original.number} archivieren`} onClick={() => onArchive(row.original)}>
              <Archive aria-hidden="true" />
            </Button>
          </span>
        ) : row.original.archived_at ? (
          <span className="numeric text-xs text-muted-foreground">seit {formatDate(row.original.archived_at)}</span>
        ) : null,
    },
  ];
}

/* ------------------------------------------------------------------ Versorger */

function SuppliersTab() {
  const api = useApi();
  const { data: me } = useMe();
  const canManage = hasPermission(me, 'meters.manage');
  const { data, error, isPending, refetch } = useQuery(adminQueries.meterSuppliers(api));
  const [editing, setEditing] = useState(/** @type {any} */ (undefined));

  return (
    <div className="flex flex-col gap-3">
      {canManage && (
        <Button size="sm" className="w-fit" onClick={() => setEditing(null)}>
          <Plus aria-hidden="true" /> Versorger anlegen
        </Button>
      )}
      {error ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : isPending ? (
        <Skeleton className="h-40 w-full" />
      ) : data.length === 0 ? (
        <EmptyState title="Keine Versorger" description="Es sind noch keine Versorger angelegt." />
      ) : (
        <Table aria-label="Versorger" containerClassName="rounded-lg border bg-card">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead data-numeric="true">Aktive Zähler</TableHead>
              {canManage && <TableHead className="sr-only">Aktionen</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((supplier) => (
              <TableRow key={supplier.id}>
                <TableCell className="font-medium">{supplier.name}</TableCell>
                <TableCell data-numeric="true">{formatNumber(supplier.meters_count ?? 0)}</TableCell>
                {canManage && (
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(supplier)} aria-label={`${supplier.name} umbenennen`}>
                      <Pencil aria-hidden="true" /> Umbenennen
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <SupplierDialog supplier={editing} open={editing !== undefined} onOpenChange={(open) => !open && setEditing(undefined)} />
    </div>
  );
}

function SupplierDialog({ supplier, open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>{open && <SupplierForm supplier={supplier} onDone={() => onOpenChange(false)} />}</DialogContent>
    </Dialog>
  );
}

function SupplierForm({ supplier, onDone }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const form = useForm({ resolver: zodResolver(nameSchema), defaultValues: { name: supplier?.name ?? '' } });
  const onSubmit = form.handleSubmit(async ({ name }) => {
    try {
      if (supplier) await api.admin.meterSuppliers.update(supplier.id, { name: name.trim() });
      else await api.admin.meterSuppliers.create({ name: name.trim() });
      await queryClient.invalidateQueries({ queryKey: keys.admin.resource('meter-suppliers') });
      toast.success(supplier ? 'Versorger umbenannt' : 'Versorger angelegt');
      onDone();
    } catch (error) {
      applyProblemToForm(error, form.setError, { fields: ['name'] });
    }
  });
  return (
    <Form {...form}>
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <DialogHeader>
          <DialogTitle>{supplier ? 'Versorger umbenennen' : 'Versorger anlegen'}</DialogTitle>
          <DialogDescription>Der Name muss eindeutig sein.</DialogDescription>
        </DialogHeader>
        <FormRootError />
        <InputField name="name" label="Name" autoComplete="off" />
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onDone}>
            Abbrechen
          </Button>
          <Button type="submit" loading={form.formState.isSubmitting}>
            Speichern
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
